// Forms data + mutations (PRD §4.13). Forms are read from tenants/{t}/forms
// (members read); create/update/delete go through owner/admin callables. The
// public fill page uses the publicForm HTTP endpoint (no auth).
import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, publicFormUrl } from './firebase';
import { useTenant } from './tenant';

export type FieldType = 'text' | 'phone' | 'email' | 'number';
export interface FormField { id: string; label: string; type: FieldType; required: boolean; }
export interface WaForm {
  id: string;
  name: string;
  enabled: boolean;
  fields: FormField[];
  submissions: number;
  createdAt: number;
}

export function useForms(): { forms: WaForm[]; loading: boolean } {
  const { tenantId } = useTenant();
  const [forms, setForms] = useState<WaForm[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setForms([]); setLoading(false); return; }
    const q = query(collection(db, `tenants/${tenantId}/forms`), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      setForms(snap.docs.map((d) => {
        const f = d.data() as Partial<WaForm>;
        return {
          id: d.id, name: f.name ?? '', enabled: f.enabled ?? false,
          fields: f.fields ?? [], submissions: f.submissions ?? 0, createdAt: f.createdAt ?? 0,
        };
      }));
      setLoading(false);
    }, () => setLoading(false));
  }, [tenantId]);

  return { forms, loading };
}

export interface FormInput { id?: string; name: string; enabled: boolean; fields: FormField[]; }
const saveFn = httpsCallable<FormInput, { ok: boolean; id: string }>(functions, 'formSave');
const deleteFn = httpsCallable<{ id: string }, { ok: boolean }>(functions, 'formDelete');

export function saveForm(input: FormInput) { return saveFn(input).then((r) => r.data); }
export function deleteForm(id: string) { return deleteFn({ id }).then((r) => r.data); }

/** Public share link for a form (the SPA fill page). */
export function formFillUrl(tenantId: string, formId: string): string {
  return `${window.location.origin}/f/${tenantId}/${formId}`;
}

// ── public fill page helpers (no auth) ───────────────────────────────────────
export async function fetchPublicForm(tenantId: string, formId: string): Promise<{ name: string; fields: FormField[] }> {
  const r = await fetch(`${publicFormUrl}?tenantId=${encodeURIComponent(tenantId)}&formId=${encodeURIComponent(formId)}`);
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? 'error');
  return r.json();
}

export async function submitPublicForm(tenantId: string, formId: string, values: Record<string, string>): Promise<void> {
  const r = await fetch(publicFormUrl, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, formId, values }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? 'submit_failed');
}
